import { InfoCircleFilled } from '@ant-design/icons';
import { useState } from 'react';

import { CompanyFragment } from '@willow/graphql-iso/src/portal';
import { phoneNumberFormat } from '@willow/shared-iso';
import { paymentAmountFormat } from '@willow/shared-web';

import { WillowModal } from '../../modal/Modal';

import './PayoffRequiredModal.scss';

interface Props {
  showModal: boolean;
  onClose: () => void;
  paymentAmount: number;
  company: CompanyFragment;
  hasOpenPayoff: boolean;
  onConfirm: () => Promise<void>;
}

export const PayoffRequiredModal = ({
  showModal,
  onClose,
  paymentAmount,
  company,
  hasOpenPayoff,
  onConfirm,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const phone = company.supportPhone;

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WillowModal showModal={showModal} onClose={onClose}>
      <div className="payoff-required-modal">
        <h2>
          <InfoCircleFilled className="u-color-blue1 me-2" />A payoff statement is required to pay your loan in full.
        </h2>

        <div className="payoff-required-modal__copy">
          {hasOpenPayoff ? (
            <p>
              A payoff statement already exists for this loan. To pay off your loan, please{' '}
              <b>follow the payment instructions on your payoff statement</b> or contact your loan servicer
              {phone && (
                <>
                  {' '}
                  at <a href={`tel:${phone}`}>{phoneNumberFormat(phone)}</a>
                </>
              )}
              . Payments submitted through this portal are not applied as payoff payments.
            </p>
          ) : (
            <p>
              If you would like to pay off your loan, please <b>request a payoff statement</b> from your loan servicer
              {phone && (
                <>
                  {' '}
                  at <a href={`tel:${phone}`}>{phoneNumberFormat(phone)}</a>
                </>
              )}{' '}
              or from the Manage Loan page of this loan portal (if available).
            </p>
          )}
          <p>
            If you submit this payment, the full payment of <b>{paymentAmountFormat(paymentAmount)}</b> will be placed
            in an unapplied suspense balance and will not be applied to your loan. Your principal balance will remain
            unchanged until your loan servicer processes the payoff.
          </p>
        </div>

        <div className="payoff-required-modal__buttons">
          <button type="button" onClick={onClose} className="payoff-required-modal__cancel-btn">
            Cancel
          </button>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleConfirm}
            className="payoff-required-modal__submit-btn"
            data-ghost="portal-loan--modal-confirm-payoff-suspense-button"
          >
            {isSubmitting ? 'Processing...' : 'Confirm payment'}
          </button>
        </div>
      </div>
    </WillowModal>
  );
};
